import React, { useEffect, useMemo } from 'react';
import useKeyboardFormAnimation from 'src/presentation/hooks/useKeyboardFormAnimation';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import { View, StyleSheet, Animated, ScrollView, Platform } from 'react-native';
import type { ImageStyle } from 'react-native';
import type { RouteProp } from '@react-navigation/native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Heading from 'src/presentation/components/atoms/Heading';
import ChangePasswordForm from 'src/presentation/components/organisms/forms/ChangePasswordForm';
import { useFormStore } from 'src/domain/stores/useFormStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import Images from 'src/core/utils/Images';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Constants from 'src/core/utils/Constants';

function ChangePasswordScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenChangePassword>>();
	const formStore = useFormStore();
	const eventStore = useEventStore();
	const { keyboardHeight, imageHeight } = useKeyboardFormAnimation();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo((): { img: ImageStyle } => ({ img: { width: contentWidth } }), [contentWidth]);

	// The reset token arrives in the query and React Navigation has already read it into params by
	// now, so the copy in the address bar is only sitting there to be read over a shoulder, kept in
	// browser history and handed to anything the page loads as a referrer. Take it out; the form
	// keeps working from params. A refresh does lose it, which is what a one-time token deserves.
	useEffect(() => {
		if (Platform.OS !== 'web' || typeof window === 'undefined') return;
		if (!window.location.search.includes('token=')) return;

		window.history.replaceState(window.history.state, '', window.location.pathname);
	}, []);

	return (
		<View style={styles.root}>
			{formStore.loginFromForm ? (
				<NavBar
					backBtn={true}
					bgImage={eventStore.activeTheme.header_bg_image}
					bgcolor={eventStore.activeTheme.header_bg_color}
					color={eventStore.activeTheme.header_text_color}
					editBtn={false}
					statusBarStyle={eventStore.activeTheme.statusbar_color}
					title={Translations.titleChangePassword}
				/>
			) : (
				<NavBar
					backBtn={true}
					bgcolor={Colors.primary}
					centerTitle={true}
					color={Colors.black}
					editBtn={false}
					title={Translations.titleChangePassword}
					onBackBtn={() => {
						navigation.replace(Constants.screenLogin);
					}}
				/>
			)}
			<ScrollView keyboardShouldPersistTaps="always" style={styles.scroll}>
				<Animated.View style={{ paddingBottom: keyboardHeight }}>
					<Animated.Image
						resizeMode="cover"
						source={Images.loginHeader}
						style={[styles.img, sizedStyles.img, { height: imageHeight }]}
					/>
					<View style={styles.contentContainer}>
						<Heading styles={styles.title} text={Translations.titleChangePassword} />
					</View>
					<ChangePasswordForm route={route} />
				</Animated.View>
			</ScrollView>
		</View>
	);
}

export default ChangePasswordScreen;

const styles = StyleSheet.create({
	root: {
		flex: 1,
	},
	scroll: {
		backgroundColor: Colors.white,
	},
	img: {
		height: 200,
	},
	contentContainer: {
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		paddingTop: Dimens.padding20,
	},
	title: {},
});
